import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import AddCharacterModal from '../../../components/ui/AddCharacterModal';

// Sortable Character Item Component
function SortableCharacterItem({ id, character, onRemove, totalCount }) {
  const navigate = useNavigate();
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: id });

  const style = {
    transform: CSS?.Transform?.toString(transform),
    transition,
  };

  // Enhanced existing character detection - properly handle UUIDs from database
  const isExistingCharacter = character?.id && 
    (typeof character?.id === 'string' && character?.id?.length === 36) && // UUID format
    character?.character_name?.trim() && 
    !character?.id?.toString()?.startsWith('temp-') && // Not temporary ID
    character?.id !== Date.now() && // Not timestamp-based temporary ID
    !Number.isInteger(character?.id); // Not a simple number (temporary IDs)

  const handleCharacterClick = (e) => {
    // Prevent any form submission or navigation if not an existing character
    e?.preventDefault();
    e?.stopPropagation();
    
    // Only navigate if the character exists in database
    if (isExistingCharacter) {
      // Navigate to role-detail-page with the role ID
      navigate(`/role-detail-page?id=${character?.id}`);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        p-4 
        bg-white bg-opacity-5 border border-white border-opacity-10 rounded-xl
        transition-all duration-200
        ${isDragging ? 'bg-opacity-20 shadow-xl z-10' : 'hover:bg-opacity-10'}
      `}
    >
      <div className="flex items-center space-x-3">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="p-2 text-blue-200 hover:text-white hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors cursor-grab active:cursor-grabbing"
          role="button"
          tabIndex={0}
          aria-label="Drag to reorder"
        >
          <Icon name="GripVertical" size={18} />
        </div>
        
        {/* Character Content */}
        <div className="flex-1">
          {isExistingCharacter ? (
            // Display existing character as clickable button
            <div
              onClick={handleCharacterClick}
              role="button"
              tabIndex={0}
              className="w-full text-left bg-white bg-opacity-10 border border-white border-opacity-30 rounded-xl px-4 py-3 text-white hover:bg-opacity-20 transition-all duration-200 flex items-center justify-between group cursor-pointer"
              onKeyDown={(e) => {
                if (e?.key === 'Enter' || e?.key === ' ') {
                  handleCharacterClick(e);
                }
              }}
            >
              <div className="flex items-center space-x-3">
                <Icon name="User" size={18} className="text-blue-300" />
                <span className="font-medium text-lg">{character?.character_name}</span>
                {/* Show existing character indicator */}
                <div className="px-2 py-1 bg-green-500 bg-opacity-30 border border-green-400 border-opacity-50 rounded-md">
                  <span className="text-xs text-green-200 font-medium">Existing Role</span>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-blue-200 group-hover:text-white">
                <span className="text-sm">View Role Details</span>
                <Icon name="ExternalLink" size={16} />
              </div>
            </div>
          ) : (
            // Display new character name as text (if being added)
            <div className="bg-white bg-opacity-10 border border-white border-opacity-30 rounded-xl px-4 py-3 text-white">
              <div className="flex items-center space-x-3">
                <Icon name="User" size={18} className="text-blue-300" />
                <span className="font-medium text-lg">{character?.character_name || 'Unnamed Character'}</span>
                {/* Show new character indicator */}
                <div className="px-2 py-1 bg-yellow-500 bg-opacity-30 border border-yellow-400 border-opacity-50 rounded-md">
                  <span className="text-xs text-yellow-200 font-medium">New Character</span>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced hint for existing characters */}
          {isExistingCharacter && (
            <div className="ml-7 mt-2">
              <p className="text-xs text-blue-300 opacity-75">
                <strong>Click above</strong> to manage role details, vocal range, age range, and song assignments for <strong>{character?.character_name}</strong>
              </p>
            </div>
          )}

          {/* Help text for new characters */}
          {!isExistingCharacter && character?.character_name?.trim() && (
            <div className="ml-7 mt-2">
              <p className="text-xs text-yellow-300 opacity-75">
                This character will be added to the Role database when the musical is saved
              </p>
            </div>
          )}
        </div>
        
        {/* Remove Button */}
        <button
          type="button"
          onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onRemove(id);
          }}
          disabled={totalCount <= 1}
          className={`
            p-2 rounded-lg transition-colors flex-shrink-0
            ${totalCount <= 1 
              ? 'text-gray-500 cursor-not-allowed opacity-50' :'text-red-300 hover:text-red-100 hover:bg-red-500 hover:bg-opacity-20'
            }
          `}
          aria-label="Remove character"
        >
          <Icon name="Trash2" size={16} />
        </button>
      </div>
    </div>
  );
}

const CharacterSection = ({ characters, onChange }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active?.id !== over?.id) {
      const oldIndex = characters?.findIndex(char => char?.tempId === active?.id);
      const newIndex = characters?.findIndex(char => char?.tempId === over?.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedCharacters = arrayMove(characters, oldIndex, newIndex);
        onChange?.(reorderedCharacters);
      }
    }
  };

  const handleAddCharacter = (characterName) => {
    const newCharacter = {
      id: Date.now(), // Temporary ID - will be replaced when saved to database
      character_name: characterName,
      tempId: Date.now(), // Temporary ID for drag and drop
    };
    
    onChange?.([...characters, newCharacter]);
    setIsAddModalOpen(false);
  };

  const handleRemoveCharacter = (tempId) => {
    if (characters?.length > 1) {
      const filteredCharacters = characters?.filter(char => char?.tempId !== tempId);
      onChange?.(filteredCharacters);
    }
  };

  const handleOpenAddModal = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsAddModalOpen(true);
  };

  // Add tempId to existing characters if they don't have one (for drag and drop)
  const charactersWithTempId = characters?.map(char => ({
    ...char,
    tempId: char?.tempId || char?.id || Date.now() + Math.random()
  })) || [];

  // Enhanced stats calculation - properly detect existing vs new characters
  const existingCharactersCount = charactersWithTempId?.filter(char => {
    const isExisting = char?.id && 
      (typeof char?.id === 'string' && char?.id?.length === 36) && // UUID format
      char?.character_name?.trim() && 
      !char?.id?.toString()?.startsWith('temp-') && // Not temporary ID
      char?.id !== Date.now() && // Not timestamp-based temporary ID
      !Number.isInteger(char?.id); // Not a simple number
    return isExisting;
  })?.length || 0;

  const newCharactersCount = charactersWithTempId?.length - existingCharactersCount;

  return (
    <>
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Icon name="Users" size={24} className="text-blue-200" />
            <div>
              <h2 className="text-xl font-heading font-semibold text-white">Character Management</h2>
              <div className="flex items-center space-x-4 text-blue-200 text-sm mt-1">
                {existingCharactersCount > 0 && (
                  <span className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span>{existingCharactersCount} existing role{existingCharactersCount !== 1 ? 's' : ''}</span>
                  </span>
                )}
                {newCharactersCount > 0 && (
                  <span className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                    <span>{newCharactersCount} new character{newCharactersCount !== 1 ? 's' : ''}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleOpenAddModal}
            className="border-white border-opacity-30 text-white hover:bg-white hover:bg-opacity-20 rounded-xl"
            iconName="Plus"
            iconPosition="left"
          >
            Add Character
          </Button>
        </div>

        <div className="mb-4">
          <p className="text-blue-100 text-sm">
            Manage character names for this musical. <strong>Existing characters (with green "Existing Role" badge) are clickable</strong> to view and edit their detailed role information including vocal range, age range, and song assignments. 
            Drag and drop to reorder characters as needed.
          </p>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={charactersWithTempId?.map(char => char?.tempId) || []}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {charactersWithTempId?.map((character) => (
                <SortableCharacterItem
                  key={character?.tempId}
                  id={character?.tempId}
                  character={character}
                  onRemove={handleRemoveCharacter}
                  totalCount={charactersWithTempId?.length}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {charactersWithTempId?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="Users" size={48} className="text-blue-300 mx-auto mb-3 opacity-50" />
            <p className="text-blue-200 mb-4">No characters added yet. Click "Add Character" to get started.</p>
            <Button
              type="button"
              variant="outline"
              onClick={handleOpenAddModal}
              className="border-white border-opacity-30 text-white hover:bg-white hover:bg-opacity-20 rounded-xl"
              iconName="Plus"
              iconPosition="left"
            >
              Add First Character
            </Button>
          </div>
        )}

        <div className="mt-6 p-4 bg-blue-500 bg-opacity-20 border border-blue-400 border-opacity-30 rounded-xl">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} className="text-blue-300 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="text-blue-200 mb-2">
                <strong>Character Types:</strong>
              </p>
              <div className="space-y-1 text-blue-300 text-xs">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span><strong>Existing Roles:</strong> Characters already in database - click to manage detailed role information</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <span><strong>New Characters:</strong> Will be added to Role database when musical is saved</span>
                </div>
              </div>
              <p className="text-blue-300 text-xs mt-2">
                Use the drag handles to reorder characters. Character order will be preserved in the role management system.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Character Modal */}
      <AddCharacterModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddCharacter={handleAddCharacter}
      />
    </>
  );
};

export default CharacterSection;